/**
 * Granularity options for routine grids
 */
import type { WeeklyAlarmRoutine, RotatingAlarmRoutine } from "@src/services/api/types";
import type { TimeRange } from "@visioni/schedules";
import { isTimeRange } from "./grid-utils";
import { formatTime24 } from "../../utils/format-time";

export type Granularity = 15 | 30 | 60;
export const DEFAULT_GRANULARITY: Granularity = 60;

export const GRANULARITY_OPTIONS: Array<{ id: Granularity; name: string }> = [
  { id: 15, name: "15m" },
  { id: 30, name: "30m" },
  { id: 60, name: "60m" },
];

/**
 * Calculate the number of time slots per day based on granularity
 */
export function getSlotsPerDay(granularity: Granularity): number {
  return (24 * 60) / granularity;
}

/**
 * Generate an array of slot indices for a given granularity
 */
export function generateSlots(granularity: Granularity): number[] {
  return Array.from({ length: getSlotsPerDay(granularity) }, (_, i) => i);
}

/**
 * Convert a slot index to hours and minutes
 */
export function slotToTime(slot: number, granularity: Granularity): { hour: number; minute: number } {
  const totalMinutes = slot * granularity;
  return {
    hour: Math.floor(totalMinutes / 60),
    minute: totalMinutes % 60,
  };
}

/**
 * Convert hours and minutes to a slot index
 */
export function timeToSlot(hour: number, minute: number, granularity: Granularity): number {
  return Math.floor((hour * 60 + minute) / granularity);
}

/**
 * Format a slot as a time string for display.
 *
 * Uses the shared 12‑hour time formatter so that all user-facing
 * time labels in grids stay consistent with the rest of the app.
 */
export function formatSlotTime(slot: number, granularity: Granularity): string {
  const { hour, minute } = slotToTime(slot, granularity);
  return formatTime24(hour, minute);
}

/**
 * Convert grid state from one granularity to another, preserving selections
 */
export function convertGridStateGranularity(
  oldState: { [key: string]: boolean },
  oldGranularity: Granularity,
  newGranularity: Granularity,
): { [key: string]: boolean } {
  if (oldGranularity === newGranularity) {
    return oldState;
  }

  const newState: { [key: string]: boolean } = {};
  const oldSlotsPerDay = getSlotsPerDay(oldGranularity);
  const newSlotsPerDay = getSlotsPerDay(newGranularity);

  // For each day, convert slots
  const daySet = new Set<string>();
  Object.keys(oldState).forEach((key) => {
    const [day] = key.split("-");
    daySet.add(day);
  });

  daySet.forEach((day) => {
    // Get all selected slots for this day in old granularity
    const oldSelectedSlots = new Set<number>();
    for (let slot = 0; slot < oldSlotsPerDay; slot++) {
      if (oldState[`${day}-${slot}`]) {
        oldSelectedSlots.add(slot);
      }
    }

    // Convert to time ranges, then back to new granularity slots
    if (oldSelectedSlots.size > 0) {
      // For each selected slot in old granularity, mark corresponding slots in new granularity
      oldSelectedSlots.forEach((oldSlot) => {
        const oldTime = slotToTime(oldSlot, oldGranularity);
        const oldTimeMinutes = oldTime.hour * 60 + oldTime.minute;
        const oldEndMinutes = oldTimeMinutes + oldGranularity;

        // Find all new slots that overlap with this old slot
        const newStartSlot = Math.floor(oldTimeMinutes / newGranularity);
        const newEndSlot = Math.ceil(oldEndMinutes / newGranularity);

        for (let newSlot = newStartSlot; newSlot < newEndSlot; newSlot++) {
          if (newSlot < newSlotsPerDay) {
            newState[`${day}-${newSlot}`] = true;
          }
        }
      });
    }
  });

  return newState;
}

/**
 * Calculate GCD of two numbers using Euclidean algorithm.
 */
function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

/**
 * Extract boundary minutes from a time range, normalized to minutes from midnight.
 * Handles 24:00 end times (represented as 1440 minutes).
 *
 * Note: For overnight ranges, we check the raw clock times for alignment.
 * The overnight handling in markTimeRangeSlots adds 24*60 for slot calculation,
 * but for granularity detection we care about the actual clock time boundaries.
 */
function getTimeRangeBoundaries(timeRange: TimeRange): number[] {
  const startMinutes = timeRange.start.hour * 60 + timeRange.start.minute;

  const endMinutes = timeRange.end.hour * 60 + timeRange.end.minute;

  return [startMinutes, endMinutes];
}

/**
 * Calculate the coarsest granularity that can represent all time boundaries in a routine.
 * Uses GCD of all boundary minutes to determine the finest common divisor,
 * then selects the largest supported granularity that divides it.
 *
 * @example
 * // 9:00-17:00 → GCD(540, 1020) = 60 → returns 60
 * // 9:15-10:15 → GCD(555, 615) = 15 → returns 15
 * // 9:00-9:30 → GCD(540, 570) = 30 → returns 30
 *
 * @param routine - The routine to analyze
 * @returns The appropriate granularity (15, 30, or 60) that can represent all time boundaries
 */
export function detectRoutineGranularity(routine: WeeklyAlarmRoutine | RotatingAlarmRoutine): Granularity {
  if (!routine) return DEFAULT_GRANULARITY;

  const boundaries: number[] = [];

  /**
   * Extract time boundaries using shared isTimeRange predicate.
   */
  const extractBoundaries = (timeRanges: unknown) => {
    const ranges = Array.isArray(timeRanges) ? timeRanges : [timeRanges];
    for (const range of ranges) {
      if (isTimeRange(range)) {
        boundaries.push(...getTimeRangeBoundaries(range));
      }
    }
  };

  // Extract boundaries based on routine type
  if (routine.kind === "weekly") {
    for (const window of routine.windows) {
      extractBoundaries(window.timeRanges);
    }
  } else if (routine.kind === "rotating") {
    for (const interval of routine.pattern) {
      if ("time_ranges" in interval && interval.time_ranges) {
        extractBoundaries(interval.time_ranges);
      }
    }
  }

  // If no time boundaries found, default to 60 minutes
  if (boundaries.length === 0) {
    return DEFAULT_GRANULARITY;
  }

  // Calculate GCD of all boundaries
  const boundaryGCD = boundaries.reduce((acc, val) => gcd(acc, val), boundaries[0]);

  // Find largest supported granularity that divides the GCD
  for (const granularity of [60, 30, 15] as const) {
    if (boundaryGCD % granularity === 0) {
      return granularity;
    }
  }

  // If GCD is not divisible by 15 (e.g., 9:07 = 547 minutes),
  // return finest granularity with a runtime warning
  console.warn(
    `Routine has time boundaries not aligned to 15-minute granularity (GCD=${boundaryGCD}). ` +
      `Defaulting to 15-minute granularity. Boundaries: ${boundaries
        .map((m) => `${Math.floor(m / 60)}:${(m % 60).toString().padStart(2, "0")}`)
        .join(", ")}`,
  );
  return 15;
}
